// leaderFeedDb.js
// Dexie-backed cache for LeaderFeed articles, fetch timestamps, and notes.
// Mirrors the shape/behavior of coachFeedDb.js (imported by
// CoachTheCoachDashboard.jsx), adapted to the "leader" namespace so the
// two features don't collide in the same browser's IndexedDB.

import Dexie from 'dexie';

const DB_NAME = 'leaderFeedDB';
// Bumped 1 → 2: whatever schema this file had when browsers first created
// leaderFeedDB no longer matches the 'key, userId' keyPath below. Editing
// an existing version's stores() in place (rather than bumping the version
// number) is exactly what Dexie can't reconcile — it throws
// "UpgradeError: Not yet support for changing primary key" instead of
// silently corrupting data. Bumping the version tells Dexie this is a real
// migration: it drops the mismatched v1 stores and recreates them fresh
// under v2. Any previously cached feed items/fetch-times/notes are lost in
// that drop (items and fetch-times just get re-fetched from RSS on next
// load; any hand-typed notes would be lost too — if that matters, this is
// the point where a real .upgrade() migration would be needed instead of
// a plain version bump, but that requires knowing the exact v1 keyPath
// that's actually sitting in affected browsers).
//
// If this error resurfaces later, it means the *stores() shape* changed
// again without a matching version bump — always bump DB_VERSION whenever
// you touch a keyPath (the part before the first comma) in stores().
const DB_VERSION = 2;
const ITEMS_STORE = 'items';
const TIMES_STORE = 'fetchTimes';
const NOTES_STORE = 'notes';

const db = new Dexie(DB_NAME);
db.version(DB_VERSION).stores({
  // Inline keyPath `key` (= `${userId}:${guid-or-topic}`) plus an indexed
  // `userId` column so per-user reads are an index lookup, not a full scan.
  [ITEMS_STORE]: 'key, userId',
  [TIMES_STORE]: 'key, userId',
  [NOTES_STORE]: 'key, userId',
});

// ==================== ITEMS ====================
export async function loadLeaderFeedItems(userId) {
  try {
    const rows = await db.table(ITEMS_STORE).where('userId').equals(userId).toArray();
    const result = {};
    rows.forEach((row) => { result[row.guid] = row.value; });
    return result;
  } catch (err) {
    console.error('[leaderFeedDb] loadLeaderFeedItems failed:', err);
    return {};
  }
}

export async function saveLeaderFeedItems(userId, items) {
  try {
    const rows = items
      .filter((item) => item.guid)
      .map((item) => ({ key: `${userId}:${item.guid}`, userId, guid: item.guid, value: item }));
    if (!rows.length) return;
    await db.table(ITEMS_STORE).bulkPut(rows);
  } catch (err) {
    console.error('[leaderFeedDb] saveLeaderFeedItems failed:', err);
  }
}

// ==================== FETCH TIMES ====================
export async function loadLeaderFetchTimes(userId) {
  try {
    const rows = await db.table(TIMES_STORE).where('userId').equals(userId).toArray();
    const result = {};
    rows.forEach((row) => { result[row.topic] = row.timestamp; });
    return result;
  } catch (err) {
    console.error('[leaderFeedDb] loadLeaderFetchTimes failed:', err);
    return {};
  }
}

export async function saveLeaderFetchTime(userId, topic, timestamp) {
  try {
    await db.table(TIMES_STORE).put({ key: `${userId}:${topic}`, userId, topic, timestamp });
  } catch (err) {
    console.error('[leaderFeedDb] saveLeaderFetchTime failed:', err);
  }
}

// ==================== NOTES ====================
export async function loadLeaderFeedNotes(userId) {
  try {
    const rows = await db.table(NOTES_STORE).where('userId').equals(userId).toArray();
    const result = {};
    rows.forEach((row) => { result[row.guid] = row.text; });
    return result;
  } catch (err) {
    console.error('[leaderFeedDb] loadLeaderFeedNotes failed:', err);
    return {};
  }
}

export async function saveLeaderFeedNote(userId, guid, text) {
  try {
    await db.table(NOTES_STORE).put({ key: `${userId}:${guid}`, userId, guid, text });
  } catch (err) {
    console.error('[leaderFeedDb] saveLeaderFeedNote failed:', err);
  }
}

export async function deleteLeaderFeedNote(userId, guid) {
  try {
    await db.table(NOTES_STORE).delete(`${userId}:${guid}`);
  } catch (err) {
    console.error('[leaderFeedDb] deleteLeaderFeedNote failed:', err);
  }
}

// ==================== ONE-TIME MIGRATION ====================
// Mirrors migrateCoachFeedFromLocalStorage: if older localStorage-based caches
// exist under a legacy key pattern, move them into IndexedDB (via Dexie) once,
// then mark migration complete so this is a no-op on every subsequent load.
export async function migrateLeaderFeedFromLocalStorage(userId) {
  const migrationFlagKey = `leaderfeed_migrated_${userId}`;
  if (localStorage.getItem(migrationFlagKey)) return;

  try {
    const legacyItemsKey = `leaderfeed_items_${userId}`;
    const legacyItems = localStorage.getItem(legacyItemsKey);
    if (legacyItems) {
      const parsed = JSON.parse(legacyItems);
      const items = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
      await saveLeaderFeedItems(userId, items);
      localStorage.removeItem(legacyItemsKey);
    }
  } catch (err) {
    console.warn('[leaderFeedDb] Migration skipped/failed (non-fatal):', err);
  } finally {
    localStorage.setItem(migrationFlagKey, '1');
  }
}

/**
 * Load all LeaderFeed notes for a user as raw rows (not the collapsed
 * guid→text map loadLeaderFeedNotes returns) — used by the backup/export
 * engine. Includes the synthetic `key` field this store's schema needs.
 * @param {string} userId
 * @returns {Promise<Array<{key: string, userId: string, guid: string, text: string}>>}
 */
export async function getAllLeaderFeedNotesForExport(userId) {
  try {
    return await db.table(NOTES_STORE).where('userId').equals(userId).toArray();
  } catch (err) {
    console.error('[leaderFeedDb] getAllLeaderFeedNotesForExport failed:', err);
    return [];
  }
}

/**
 * Bulk-restore LeaderFeed notes from a backup (raw rows, same shape as
 * getAllLeaderFeedNotesForExport returns — must include `key`).
 * @param {Array<{key: string, userId: string, guid: string, text: string}>} rows
 */
export async function bulkPutLeaderFeedNotes(rows) {
  if (!rows || rows.length === 0) return;
  try {
    await db.table(NOTES_STORE).bulkPut(rows);
  } catch (err) {
    console.error('[leaderFeedDb] bulkPutLeaderFeedNotes failed:', err);
  }
}